import { useEffect, useRef, useState } from "react";

/**
 * Renders a single line of text that scrolls horizontally only when it would
 * otherwise overflow its container. Short titles sit perfectly static. Long
 * titles use a CSS-only continuous marquee that is masked inside the parent.
 */
export default function MarqueeText({
  text,
  className = "",
  testid,
  speedPxPerSec = 38,
}) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [duration, setDuration] = useState(20);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    const measure = () => {
      // Use the first child width since we render the text twice for a
      // seamless loop when overflowing.
      const child = inner.firstElementChild;
      const childW = child ? child.scrollWidth : inner.scrollWidth;
      const wrapW = wrap.clientWidth;
      const isOverflow = childW > wrapW + 4;
      setOverflow(isOverflow);
      if (isOverflow) {
        // Distance to travel = width of one copy + a small gap baked into CSS.
        const gap = 48; // px
        const d = Math.max(8, (childW + gap) / speedPxPerSec);
        setDuration(d);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    if (inner.firstElementChild) ro.observe(inner.firstElementChild);
    return () => ro.disconnect();
  }, [text, speedPxPerSec]);

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      className={`marquee-mask relative overflow-hidden ${className}`}
    >
      <div
        ref={innerRef}
        className={`whitespace-nowrap ${overflow ? "marquee-track" : ""}`}
        style={overflow ? { animationDuration: `${duration}s` } : undefined}
      >
        <span className="inline-block pr-12">{text}</span>
        {overflow && (
          <span aria-hidden className="inline-block pr-12">
            {text}
          </span>
        )}
      </div>
    </div>
  );
}
